import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiLock, FiArrowLeft } from 'react-icons/fi';
import { useCart } from '../../contexts/CartContext';
import {
  computePayableTotalInr,
  formatInr,
} from '../../utils/pricing';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    apartment: '',
    city: '',
    country: 'IN',
    state: '',
    zipCode: '',
    shippingMethod: 'standard',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    billingSameAsShipping: true,
    billingAddress: {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
    },
  });

  const payable = useMemo(
    () => computePayableTotalInr(cart.items || []),
    [cart.items]
  );
  const tax = Math.round(payable * 0.08);
  const total = payable + tax;

  const shippingMethods = [
    { id: 'standard', name: 'Standard', price: 0, estimated: '5–7 days' },
    { id: 'express', name: 'Express', price: 0, estimated: '2–3 days' },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('billing.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        billingAddress: {
          ...prev.billingAddress,
          [field]: value,
        },
      }));
    } else if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleNextStep = () => {
    if (step === 1) {
      const requiredFields = [
        'firstName',
        'lastName',
        'email',
        'address',
        'city',
        'state',
        'zipCode',
      ];
      const isValid = requiredFields.every((field) => formData[field].trim() !== '');
      if (!isValid) {
        alert('Please fill in all required fields');
        return;
      }
      if (!formData.shippingMethod) {
        alert('Please select delivery option');
        return;
      }
    }
    setStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmitOrder = async () => {
    setLoading(true);
    const paymentFields = ['cardNumber', 'cardName', 'expiryDate', 'cvv'];
    const isValid = paymentFields.every((field) => formData[field].trim() !== '');
    if (!isValid) {
      alert('Please fill in all payment information');
      setLoading(false);
      return;
    }
    setTimeout(() => {
      setLoading(false);
      alert('Order placed successfully!');
      navigate('/');
    }, 1500);
  };

  const items = cart.items || [];

  if (items.length === 0) {
    return (
      <div className="checkout-page page-transition">
        <div className="container" style={{ padding: '3rem 1rem', textAlign: 'center' }}>
          <p>Your cart is empty.</p>
          <Link to="/products?category=t-shirts">Shop T-Shirts</Link>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div className="checkout-step">
          <h2 className="step-title">Details & delivery</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="firstName">First name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group full-width">
              <label htmlFor="address">Address *</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="apartment">Apt / suite</label>
              <input
                type="text"
                id="apartment"
                name="apartment"
                value={formData.apartment}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="country">Country *</label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                required
              >
                <option value="IN">India</option>
                <option value="US">United States</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="state">State *</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="zipCode">PIN / ZIP *</label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <h3 className="step-subtitle">Delivery</h3>
          <div className="shipping-methods">
            {shippingMethods.map((method) => (
              <label key={method.id} className="shipping-method">
                <input
                  type="radio"
                  name="shippingMethod"
                  value={method.id}
                  checked={formData.shippingMethod === method.id}
                  onChange={handleInputChange}
                />
                <div className="method-info">
                  <div className="method-name">
                    <span>{method.name}</span>
                  </div>
                  <p className="method-estimated">{method.estimated}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="checkout-step">
          <h2 className="step-title">Payment</h2>
          <div className="payment-method">
            <div className="payment-header">
              <FiLock />
              <span>Secure payment</span>
            </div>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="cardNumber">Card number *</label>
                <input
                  type="text"
                  id="cardNumber"
                  name="cardNumber"
                  value={formData.cardNumber}
                  onChange={handleInputChange}
                  placeholder="1234 5678 9012 3456"
                  required
                />
              </div>
              <div className="form-group full-width">
                <label htmlFor="cardName">Name on card *</label>
                <input
                  type="text"
                  id="cardName"
                  name="cardName"
                  value={formData.cardName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="expiryDate">Expiry *</label>
                <input
                  type="text"
                  id="expiryDate"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  placeholder="MM/YY"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cvv">CVV *</label>
                <input
                  type="text"
                  id="cvv"
                  name="cvv"
                  value={formData.cvv}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="billingSameAsShipping"
              checked={formData.billingSameAsShipping}
              onChange={handleInputChange}
            />
            <span>Billing address same as shipping</span>
          </label>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="checkout-page page-transition">
      <div className="checkout-header">
        <div className="container">
          <button type="button" className="back-to-cart" onClick={() => navigate('/cart')}>
            <FiArrowLeft /> Back to cart
          </button>
          <h1 className="page-title">Checkout</h1>
        </div>
      </div>

      <div className="container">
        <div className="checkout-container">
          <div className="checkout-progress checkout-progress--two">
            <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Details</span>
            </div>
            <div className={`progress-line ${step >= 2 ? 'active' : ''}`} />
            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Payment</span>
            </div>
          </div>

          <div className="checkout-content">
            <div className="checkout-form">
              {renderStepContent()}
              <div className="checkout-navigation">
                {step > 1 && (
                  <button type="button" className="prev-step-btn" onClick={handlePreviousStep}>
                    Back
                  </button>
                )}
                {step < 2 ? (
                  <button type="button" className="next-step-btn" onClick={handleNextStep}>
                    Continue to payment
                  </button>
                ) : (
                  <button
                    type="button"
                    className="place-order-btn"
                    onClick={handleSubmitOrder}
                    disabled={loading}
                  >
                    {loading ? 'Processing…' : 'Place order'}
                  </button>
                )}
              </div>
            </div>

            <div className="checkout-summary">
              <div className="summary-card">
                <h2 className="summary-title">Order summary</h2>
                <div className="order-items">
                  {items.map((item) => (
                    <div key={item._id} className="order-item">
                      <div className="item-info">
                        <span className="item-name">{item.name}</span>
                        <span className="item-quantity">×{item.quantity}</span>
                      </div>
                      <span className="item-price">
                        {formatInr(item.unitPriceInr * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="summary-totals">
                  <div className="total-row">
                    <span>Subtotal</span>
                    <span>{formatInr(payable)}</span>
                  </div>
                  <div className="total-row">
                    <span>Tax</span>
                    <span>{formatInr(tax)}</span>
                  </div>
                  <div className="total-row grand-total">
                    <span>Total</span>
                    <span>{formatInr(total)}</span>
                  </div>
                </div>
                <div className="summary-help">
                  <p>
                    Need help? <a href="mailto:support@basick.com">Contact us</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
